import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  GoogleAuthProvider,
  onAuthStateChanged,
  signInWithPopup,
  signOut,
} from "firebase/auth";
import {
  collection,
  deleteDoc,
  getDoc,
  getDocs,
  runTransaction,
  setDoc,
  doc as fsDoc,
} from "firebase/firestore";
import { fsdb, auth } from "../firebase";

const FLAVOR_CATEGORIES = {
  C1: {
    title: "Moogi's Favorite Drinks",
    flavors: [
      {
        id: "milktea",
        name: "Milk Tea",
        color: "#f3d1b9",
        desc: "Classic spiced Ceylon black tea blend with fresh dairy",
      },
      {
        id: "thaitea",
        name: "Thai Tea",
        color: "#f9a353",
        desc: "Bold steeped Thai tea leaves with condensed milk notes",
      },
      {
        id: "horchata",
        name: "Horchata",
        color: "#d6ae8b",
        desc: "Sweetened rice milk with toasted Mexican cinnamon bark",
      },
    ],
  },
  C2: {
    title: "Sweet and Savory",
    flavors: [
      {
        id: "sweetcorn",
        name: "Sweet Corn",
        color: "#ffdc5e",
        desc: "Summer sweet corn churned into creamy savory-sweet ice cream",
      },
      {
        id: "saltedcaramel",
        name: "Salted Caramel",
        color: "#fed08c",
        desc: "Deep burnt caramel swirled with flaky sea salt",
      },
      {
        id: "pistachio",
        name: "Pistachio",
        color: "#b0af34",
        desc: "Roasted Mediterranean pistachios with rich nutty aroma",
      },
    ],
  },
  C3: {
    title: "Fruity and Refreshing",
    flavors: [
      {
        id: "strawberry",
        name: "Strawberry",
        color: "#d52627",
        dots: "#000000",
        desc: "Ripe wild strawberry puree blended with velvet cream",
      },
      {
        id: "honeydew",
        name: "Honeydew",
        color: "#cce29d",
        desc: "Fragrant sweet green melon ice cream",
      },
      {
        id: "bananamilk",
        name: "Banana Milk",
        color: "#fdeba1",
        desc: "Beloved Korean nostalgic sweet banana and milk swirl",
      },
    ],
  },
  C4: {
    title: "Rich and Decadent",
    flavors: [
      {
        id: "blacksesame",
        name: "Black Sesame",
        color: "#eae9e8",
        dots: "#000000",
        desc: "Deep-roasted black sesame seeds with toasty earthy notes",
      },
      {
        id: "chocolate",
        name: "Chocolate",
        color: "#783f2f",
        desc: "Rich Dutch cocoa dark chocolate soft serve",
      },
      {
        id: "cookiebutter",
        name: "Cookie Butter",
        color: "#efd8ba",
        desc: "Spiced Belgian speculoos cookie crumble infused swirl",
      },
    ],
  },
};

const SCHEDULE_COLUMNS = [
  "JAN",
  "MAR",
  "MAY (2nd Chance)",
  "JUL",
  "SEP",
  "NOV (2nd Chance)",
];

const SCHEDULE_ROWS = [
  {
    group: "Group 1 (11 Stores)",
    cells: ["C1", "C2", "2nd Chance", "C3", "C4", "2nd Chance"],
  },
  {
    group: "Group 2 (12 Stores)",
    cells: ["C2", "C3", "2nd Chance", "C4", "C1", "2nd Chance"],
  },
  {
    group: "Group 3 (12 Stores)",
    cells: ["C3", "C4", "2nd Chance", "C1", "C2", "2nd Chance"],
  },
  {
    group: "Group 4 (11 Stores)",
    cells: ["C4", "C1", "2nd Chance", "C2", "C3", "2nd Chance"],
  },
];

const CORE_FLAVORS = [
  { id: "matcha", name: "Matcha", color: "#8cc444" },
  { id: "ube", name: "Ube", color: "#8f2883" },
  { id: "milk", name: "Milk", color: "#eee7d4" },
  { id: "cookiesncream", name: "Cookies 'n Cream", color: "#c4c4c4" },
];

const ALL_FLAVORS = Object.values(FLAVOR_CATEGORIES).flatMap((c) => c.flavors);
const FLAVOR_BY_ID = Object.fromEntries(ALL_FLAVORS.map((f) => [f.id, f]));

const VOTE_STORAGE_KEY = "somi_vote_v1";
const CLIENT_ID_KEY = "somi_client_id";

const readStoredVote = () => {
  try {
    const raw = localStorage.getItem(VOTE_STORAGE_KEY);
    const vote = raw ? JSON.parse(raw) : null;
    return vote && FLAVOR_BY_ID[vote.flavorId] ? vote : null;
  } catch {
    return null;
  }
};

const getClientId = () => {
  let clientId = localStorage.getItem(CLIENT_ID_KEY);
  if (!clientId) {
    clientId =
      "c_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem(CLIENT_ID_KEY, clientId);
  }
  return clientId;
};

const byStoreId = (a, b) =>
  String(a.storeId).localeCompare(String(b.storeId), "en", { numeric: true });

const formatSwirlDate = (iso) => {
  if (!iso) return "";
  return new Date(iso + "T00:00:00").toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const getNextMonthFirst = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth() + 1, 1, 0, 0, 0);
};

const Icon = ({ name, className = "w-5 h-5" }) => {
  const paths = {
    search: "M21 21l-4.35-4.35M11 19a8 8 0 1 1 0-16 8 8 0 0 1 0 16z",
    x: "M18 6L6 18M6 6l12 12",
    calendar:
      "M8 2v4M16 2v4M3 10h18M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z",
    check: "M22 11.08V12a10 10 0 1 1-5.93-9.14M22 4L12 14.01l-3-3",
    lock: "M19 11H5a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7a2 2 0 0 0-2-2zM7 11V7a5 5 0 0 1 10 0v4",
    clock: "M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20zM12 6v6l4 2",
    rotate: "M3 12a9 9 0 1 0 3-6.7L3 8M3 3v5h5",
  };
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={paths[name]} />
    </svg>
  );
};

const GoogleG = ({ className = "w-4 h-4" }) => (
  <svg className={className} viewBox="0 0 48 48" aria-hidden="true">
    <path
      fill="#FFC107"
      d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"
    />
    <path
      fill="#FF3D00"
      d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"
    />
    <path
      fill="#4CAF50"
      d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.91 11.91 0 0 1 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"
    />
    <path
      fill="#1976D2"
      d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"
    />
  </svg>
);

const GoogleSignInButton = ({ onClick, disabled, className = "" }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className={
      "inline-flex items-center gap-2.5 bg-white border border-slate-200 rounded-full px-5 py-2.5 text-sm font-semibold text-slate-700 shadow-xs hover:shadow-soft hover:border-slate-300 transition-all disabled:opacity-60 disabled:cursor-not-allowed " +
      className
    }
  >
    <GoogleG />
    {disabled ? "Connecting..." : "Sign in with Google"}
  </button>
);

const FlavorDot = ({ flavor, className = "w-6 h-6" }) => (
  <span
    className={
      "inline-block rounded-full shrink-0 ring-1 ring-black/10 " + className
    }
    style={{ backgroundColor: flavor.color }}
    aria-hidden="true"
  />
);

const FlavorTrio = ({ flavors, className = "w-6 h-6" }) => (
  <span className="inline-flex gap-1">
    {flavors.map((flavor) => (
      <FlavorDot key={flavor.id} flavor={flavor} className={className} />
    ))}
  </span>
);

const scheduleCellClass = (cell) => {
  if (cell === "2nd Chance")
    return "p-3 text-center bg-amber-50 font-semibold text-somi-gold";
  const color =
    cell === "C1"
      ? "text-amber-600"
      : cell === "C2"
        ? "text-yellow-600"
        : cell === "C3"
          ? "text-rose-500"
          : "text-amber-800";
  return "p-3 text-center font-semibold " + color;
};

const PickYourSomi = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [view, setView] = useState(
    ["overview", "vote", "results"].includes(searchParams.get("view"))
      ? searchParams.get("view")
      : "overview",
  );

  const selectView = useCallback(
    (nextView) => {
      setView(nextView);
      setSearchParams(nextView === "overview" ? {} : { view: nextView });
    },
    [setSearchParams],
  );

  const paramView = searchParams.get("view");
  useEffect(() => {
    if (["overview", "vote", "results"].includes(paramView)) {
      setView(paramView);
    }
  }, [paramView]);
  const [campaignMode, setCampaignMode] = useState("voting");
  const [deckOpen, setDeckOpen] = useState(false);
  const [voteModal, setVoteModal] = useState(null);
  const [userVote, setUserVote] = useState(readStoredVote);
  const [user, setUser] = useState(null);
  const [signingIn, setSigningIn] = useState(false);
  const [stateFilter, setStateFilter] = useState("ALL");
  const [resultsState, setResultsState] = useState("ALL");
  const [searchQuery, setSearchQuery] = useState("");
  const [notifyEmail, setNotifyEmail] = useState("");
  const [toast, setToast] = useState(null);
  const [timeLeft, setTimeLeft] = useState(getNextMonthFirst() - new Date());
  const [polls, setPolls] = useState({
    open: [],
    closed: [],
    latestCycle: null,
  });
  const [pollsLoading, setPollsLoading] = useState(true);
  const [pollsError, setPollsError] = useState(null);

  useEffect(() => {
    const timer = setInterval(
      () => setTimeLeft(getNextMonthFirst() - new Date()),
      1000,
    );
    return () => clearInterval(timer);
  }, []);

  const loadPolls = useCallback(async () => {
    setPollsLoading(true);
    setPollsError(null);
    try {
      const snapshot = await getDocs(collection(fsdb, "storePolls"));
      const all = snapshot.docs.map((document) => ({
        id: document.id,
        ...document.data(),
      }));
      const open = all.filter((poll) => poll.status === "open").sort(byStoreId);
      const closedWithWinners = all.filter(
        (poll) => poll.status === "closed" && poll.winningFlavorId,
      );
      const cycles = Array.from(
        new Set(closedWithWinners.map((poll) => poll.cycle)),
      ).sort();
      const latestCycle = cycles[cycles.length - 1] || null;
      const closed = closedWithWinners
        .filter((poll) => poll.cycle === latestCycle)
        .sort(byStoreId);
      setPolls({ open, closed, latestCycle });
      setPollsLoading(false);
    } catch (error) {
      setPollsError(error.message);
      setPollsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPolls();
  }, [loadPolls]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (account) => setUser(account));
    return () => unsubscribe();
  }, []);

  const currentCycle = useMemo(
    () => polls.open.find((poll) => poll.status === "open")?.cycle || null,
    [polls],
  );

  // Vote refresh: when a new poll set is live, any stored ballot from an old
  // cycle is stale - clear it so every past voter can vote in the new cycle.
  useEffect(() => {
    if (!currentCycle) return;
    const stored = readStoredVote();
    if (stored && stored.cycle !== currentCycle) {
      localStorage.removeItem(VOTE_STORAGE_KEY);
      setUserVote(null);
    }
  }, [currentCycle]);

  useEffect(() => {
    if (!user || !currentCycle) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const cycleVoteRef = fsDoc(
          fsdb,
          "pollSubmissions",
          user.uid + "_" + currentCycle,
        );
        const cycleVoteSnap = await getDoc(cycleVoteRef);
        if (cancelled) return;
        if (cycleVoteSnap.exists()) {
          const data = cycleVoteSnap.data();
          if (FLAVOR_BY_ID[data.flavorId]) {
            localStorage.setItem(VOTE_STORAGE_KEY, JSON.stringify(data));
            setUserVote(data);
          }
          return;
        }
        const legacyRef = fsDoc(fsdb, "pollSubmissions", user.uid);
        const legacySnap = await getDoc(legacyRef);
        if (cancelled) return;
        if (legacySnap.exists() && legacySnap.data().cycle === currentCycle) {
          const migrated = { ...legacySnap.data(), uid: user.uid };
          await setDoc(cycleVoteRef, migrated);
          if (cancelled) return;
          localStorage.setItem(VOTE_STORAGE_KEY, JSON.stringify(migrated));
          setUserVote(migrated);
          return;
        }
        const stored = readStoredVote();
        if (stored && stored.cycle === currentCycle) setUserVote(stored);
      } catch {
        const stored = readStoredVote();
        if (stored && stored.cycle === currentCycle) setUserVote(stored);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, currentCycle]);

  useEffect(() => {
    if (!toast) return undefined;
    const timeout = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(timeout);
  }, [toast]);

  const countdown = useMemo(() => {
    const totalSeconds = Math.max(0, Math.floor(timeLeft / 1000));
    return {
      days: Math.floor(totalSeconds / 86400),
      hours: Math.floor((totalSeconds % 86400) / 3600),
      minutes: Math.floor((totalSeconds % 3600) / 60),
      seconds: totalSeconds % 60,
    };
  }, [timeLeft]);

  const votedFlavor = userVote ? FLAVOR_BY_ID[userVote.flavorId] : null;
  const votedStoreName = userVote?.storeName || null;

  const voteStates = useMemo(
    () => [
      "ALL",
      ...Array.from(new Set(polls.open.map((poll) => poll.state))).sort(),
    ],
    [polls],
  );

  const filteredPolls = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return polls.open.filter((poll) => {
      const matchesState = stateFilter === "ALL" || poll.state === stateFilter;
      const matchesQuery =
        !q ||
        poll.storeName.toLowerCase().includes(q) ||
        poll.city.toLowerCase().includes(q);
      return matchesState && matchesQuery;
    });
  }, [polls, searchQuery, stateFilter]);

  const resultStates = useMemo(
    () => [
      "ALL",
      ...Array.from(new Set(polls.closed.map((poll) => poll.state))).sort(),
    ],
    [polls],
  );

  const filteredResults = useMemo(
    () =>
      polls.closed.filter(
        (poll) => resultsState === "ALL" || poll.state === resultsState,
      ),
    [polls, resultsState],
  );

  const openVoteModal = (poll) => {
    if (userVote) {
      setToast(
        "You have already voted for " +
          (votedStoreName || "another store") +
          ". Only 1 vote is allowed per customer.",
      );
      return;
    }
    setVoteModal({ store: poll, submitted: false });
  };

  const handleSignIn = async () => {
    if (signingIn) return;
    setSigningIn(true);
    try {
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({ prompt: "select_account" });
      await signInWithPopup(auth, provider);
    } catch {
      setToast("Google sign-in was cancelled or blocked. Please try again.");
    } finally {
      setSigningIn(false);
    }
  };

  const handleSignOut = async () => {
    await signOut(auth);
    setToast("Signed out. Sign in again any time to see your ballot.");
  };

  const castVote = async (flavorId) => {
    if (!user) {
      setToast("Please sign in with Google to cast your vote.");
      return;
    }
    const poll = voteModal.store;
    if (!poll.options || !poll.options.includes(flavorId)) return;
    const vote = {
      pollId: poll.id,
      storeId: poll.storeId,
      storeName: poll.storeName,
      cycle: poll.cycle,
      category: poll.category,
      flavorId,
      uid: user.uid,
      clientId: getClientId(),
      createdAt: new Date().toISOString(),
    };
    try {
      const voteRef = fsDoc(
        fsdb,
        "pollSubmissions",
        user.uid + "_" + poll.cycle,
      );
      const existing = await runTransaction(fsdb, async (transaction) => {
        const snapshot = await transaction.get(voteRef);
        if (snapshot.exists()) return snapshot.data();
        await transaction.set(voteRef, vote);
        return null;
      });
      if (existing) {
        if (FLAVOR_BY_ID[existing.flavorId]) {
          localStorage.setItem(VOTE_STORAGE_KEY, JSON.stringify(existing));
          setUserVote(existing);
        }
        setVoteModal({ store: poll, submitted: true });
        setToast(
          "This Google account has already voted this cycle. 1 vote per account!",
        );
        return;
      }
      localStorage.setItem(VOTE_STORAGE_KEY, JSON.stringify(vote));
      setUserVote(vote);
      setVoteModal({ store: poll, submitted: true });
      setToast("Thank you for your vote! Stay tuned for the results.");
    } catch {
      setToast("Could not submit your vote. Please try again.");
    }
  };

  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  const [resettingVote, setResettingVote] = useState(false);

  const confirmResetVote = async () => {
    if (resettingVote) return;
    setResettingVote(true);
    try {
      if (user) {
        await deleteDoc(
          fsDoc(fsdb, "pollSubmissions", user.uid + "_" + userVote.cycle),
        );
      } else if (userVote && userVote.clientId) {
        await deleteDoc(
          fsDoc(
            fsdb,
            "pollSubmissions",
            userVote.pollId + "_" + userVote.clientId,
          ),
        );
      }
      localStorage.removeItem(VOTE_STORAGE_KEY);
      setUserVote(null);
      setResetConfirmOpen(false);
      setToast(
        "Your ballot was reset. You can vote again while polls are open.",
      );
    } catch {
      setToast("Could not reset your ballot. Please try again.");
    } finally {
      setResettingVote(false);
    }
  };

  const submitNotify = () => {
    setToast("You are on the alert notification list!");
    setNotifyEmail("");
  };

  const pad = (value) => String(value).padStart(2, "0");

  const tabBar = [
    ["overview", "📘", "Rules & Overview"],
    ["vote", "🗳️", "Vote By Store (46)"],
    ["results", "🍦", "Store Winners"],
  ];

  return (
    <div className="fish-pattern font-jakarta text-slate-800">
      <div className="bg-somi-charcoal text-amber-50/90 text-center text-[11px] md:text-xs py-2 px-4">
        <span className="font-semibold text-somi-moogi uppercase tracking-wider">
          Official 2027 Program
        </span>
        <span className="hidden md:inline">
          {" "}
          · New customer-voted rotation system ·{" "}
        </span>
        <span className="hidden md:inline">Effective January 1, 2027 · </span>
        <button
          onClick={() => setDeckOpen(true)}
          className="underline underline-offset-2 hover:text-somi-moogi font-semibold"
        >
          Meeting Deck Guide
        </button>
      </div>

      <header className="max-w-7xl mx-auto px-4 md:px-8 pt-10 md:pt-14 pb-8 text-center">
        <span className="inline-flex items-center gap-2 bg-amber-100 border border-amber-200 text-somi-waffle text-[11px] font-bold uppercase tracking-widest px-4 py-1.5 rounded-full">
          ✨ SOMISOMI 2027 Flavor Rotation System
        </span>
        <h1 className="font-display font-bold text-4xl md:text-6xl text-slate-900 mt-5 leading-tight">
          Pick Your <span className="text-somi-waffle">Somi</span>
        </h1>
        <p className="text-slate-600 max-w-2xl mx-auto mt-4 text-sm md:text-base leading-relaxed">
          Starting January 1, 2027, SomiSomi introduces a customer-voted
          rotating flavor system. While our signature core flavors remain
          permanent, customers at our 46 locations vote to decide what fills the
          rotational dispenser slots at their local store!
        </p>

        <div className="flex flex-wrap items-center justify-center gap-2 mt-8">
          {tabBar.map(([id, emoji, label]) => (
            <button
              key={id}
              onClick={() => selectView(id)}
              className={
                view === id
                  ? "px-4 py-2 rounded-full text-sm font-semibold transition-all whitespace-nowrap bg-somi-waffle text-white shadow-soft"
                  : "px-4 py-2 rounded-full text-sm font-semibold transition-all whitespace-nowrap bg-white text-slate-600 hover:bg-amber-50 border border-amber-200/80"
              }
            >
              {emoji} {label}
            </button>
          ))}
        </div>

        <div className="flex justify-center mt-5 px-4">
          {user ? (
            <div className="inline-flex items-center gap-2 bg-white border border-amber-200/80 rounded-full pl-1.5 pr-3 py-1.5 shadow-xs max-w-full overflow-hidden">
              {user.photoURL ? (
                <img
                  src={user.photoURL}
                  alt=""
                  className="w-7 h-7 rounded-full ring-1 ring-amber-100 shrink-0"
                  referrerPolicy="no-referrer"
                />
              ) : (
                <span className="w-7 h-7 rounded-full bg-amber-100 text-somi-waffle text-xs font-bold flex items-center justify-center shrink-0">
                  {(user.displayName || user.email || "?")
                    .charAt(0)
                    .toUpperCase()}
                </span>
              )}
              <span className="text-xs font-semibold text-slate-700 min-w-0 truncate max-w-[110px] sm:max-w-[160px]">
                {user.displayName || user.email}
              </span>
              <button
                onClick={handleSignOut}
                className="text-[11px] font-bold text-slate-400 hover:text-slate-600 whitespace-nowrap leading-none px-2 py-1.5 shrink-0"
              >
                Sign out
              </button>
            </div>
          ) : (
            <div className="inline-flex items-center gap-2">
              <GoogleSignInButton onClick={handleSignIn} disabled={signingIn} />
              <span className="text-[11px] text-slate-400 hidden sm:inline">
                1 vote per Google account
              </span>
            </div>
          )}
        </div>
      </header>

      {view === "overview" && (
        <main className="max-w-7xl mx-auto px-4 md:px-8 pb-20">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-12">
            {[
              ["🗳️", "1 Vote", "per customer during the window"],
              ["📍", "46 Stores", "divided into 4 groups"],
              ["🍦", "12 Flavors", "across 4 categories"],
              ["⚡", "2nd Chance", "elections in May & November"],
            ].map(([emoji, stat, sub]) => (
              <div
                key={stat}
                className="bg-white rounded-2xl p-4 text-center shadow-card border border-amber-100"
              >
                <div className="text-2xl">{emoji}</div>
                <div className="font-display font-bold text-lg text-slate-900 mt-1">
                  {stat}
                </div>
                <div className="text-[11px] text-slate-500">{sub}</div>
              </div>
            ))}
          </div>

          <section className="mb-14">
            <span className="inline-block text-[11px] font-bold uppercase tracking-widest bg-amber-100 text-somi-gold px-3 py-1 rounded-full mb-3">
              1. Store Setup (Daily Baseline)
            </span>
            <h2 className="font-display font-bold text-2xl md:text-3xl text-slate-900">
              How The Rotational Slots Work In Stores
            </h2>
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 text-sm text-amber-900 mb-6 max-w-2xl">
              ⚡ <strong>Core Flavors are Permanent:</strong> Matcha, Ube,
              Cookies 'n Cream (+ Milk in 3-machine setups).
            </div>

            <div className="grid md:grid-cols-2 gap-5">
              <div className="bg-white rounded-3xl p-6 shadow-card border border-amber-100">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-display font-bold text-xl text-slate-900">
                    3 Machine Stores
                  </h3>
                  <span className="text-[11px] font-bold px-3 py-1 rounded-full bg-amber-100 text-somi-waffle">
                    6 Flavors Total
                  </span>
                </div>
                <ul className="space-y-3">
                  <li className="flex gap-2.5 text-sm text-slate-600">
                    <span className="text-somi-gold mt-0.5">✔</span>
                    <span>
                      4 Core Flavors (Permanent):
                      <span className="flex flex-wrap gap-x-4 gap-y-1.5 mt-1">
                        {CORE_FLAVORS.map((flavor) => (
                          <span
                            key={flavor.id}
                            className="inline-flex items-center gap-1.5"
                          >
                            <FlavorDot flavor={flavor} className="w-6 h-6" />
                            <span className="text-xs font-medium text-slate-700">
                              {flavor.name}
                            </span>
                          </span>
                        ))}
                      </span>
                    </span>
                  </li>
                  <li className="flex gap-2.5 text-sm text-slate-600">
                    <span className="text-somi-gold mt-0.5">✔</span>
                    🗳️ Slot 1: Customer-Voted (2-month run)
                  </li>
                  <li className="flex gap-2.5 text-sm text-slate-600">
                    <span className="text-somi-gold mt-0.5">✔</span>
                    🍁 Slot 2: Seasonal Flavor (2-month run)
                  </li>
                  <li className="flex gap-2.5 text-sm text-slate-600">
                    <span className="text-somi-gold mt-0.5">✔</span>
                    Voted + Seasonal run at the same time and change monthly on
                    a staggered 2-month cycle!
                  </li>
                </ul>
              </div>

              <div className="bg-white rounded-3xl p-6 shadow-card border border-amber-100">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-display font-bold text-xl text-slate-900">
                    2 Machine Stores
                  </h3>
                  <span className="text-[11px] font-bold px-3 py-1 rounded-full bg-slate-100 text-slate-600">
                    4 Flavors Total
                  </span>
                </div>
                <ul className="space-y-3">
                  <li className="flex gap-2.5 text-sm text-slate-600">
                    <span className="text-somi-gold mt-0.5">✔</span>
                    <span>
                      3 Core Flavors (Permanent):
                      <span className="flex flex-wrap gap-x-4 gap-y-1.5 mt-1">
                        {CORE_FLAVORS.filter(
                          (flavor) => flavor.id !== "milk",
                        ).map((flavor) => (
                          <span
                            key={flavor.id}
                            className="inline-flex items-center gap-1.5"
                          >
                            <FlavorDot flavor={flavor} className="w-6 h-6" />
                            <span className="text-xs font-medium text-slate-700">
                              {flavor.name}
                            </span>
                          </span>
                        ))}
                      </span>
                    </span>
                  </li>
                  <li className="flex gap-2.5 text-sm text-slate-600">
                    <span className="text-somi-gold mt-0.5">✔</span>
                    🗳️ Customer-Voted{" "}
                    <span className="text-slate-400 mx-1">OR</span> 🍁 Seasonal
                    Flavor
                  </li>
                  <li className="flex gap-2.5 text-sm text-slate-600">
                    <span className="text-somi-gold mt-0.5">✔</span>
                    Only one rotational flavor runs at a time, switching
                    month-to-month (Jan: Seasonal, Feb: Voted, Mar: Seasonal,
                    Apr: Voted...).
                  </li>
                </ul>
              </div>
            </div>
          </section>

          <section className="mb-14">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6">
              <div>
                <span className="inline-block text-[11px] font-bold uppercase tracking-widest bg-purple-100 text-purple-700 px-3 py-1 rounded-full mb-3">
                  3. The Voting System Master List
                </span>
                <h2 className="font-display font-bold text-2xl md:text-3xl text-slate-900">
                  12 Flavor Contenders Across 4 Categories
                </h2>
                <p className="text-slate-500 text-sm mt-1">
                  Each store group votes from one curated 3-flavor category per
                  cycle.
                </p>
              </div>
              <div className="flex items-center gap-2 bg-white rounded-full px-4 py-2 shadow-card border border-amber-100 self-start">
                <span className="w-7 h-7 rounded-full bg-amber-400 text-slate-900 font-bold flex items-center justify-center text-sm">
                  🐣
                </span>
                <span className="text-xs font-bold text-slate-800">
                  Curated by Moogi
                </span>
              </div>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {Object.entries(FLAVOR_CATEGORIES).map(([key, category]) => (
                <div
                  key={key}
                  className="bg-white rounded-3xl p-5 shadow-card border border-amber-100"
                >
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-display font-bold text-lg text-slate-900">
                      {category.title}
                    </h3>
                    <FlavorTrio flavors={category.flavors} />
                  </div>
                  <div className="space-y-3">
                    {category.flavors.map((flavor) => (
                      <div
                        key={flavor.id}
                        className="bg-amber-50/70 rounded-2xl p-3"
                      >
                        <div className="flex items-center gap-2">
                          <FlavorDot flavor={flavor} className="w-6 h-6" />
                          <span className="font-bold text-sm text-slate-800">
                            {flavor.name}
                          </span>
                        </div>
                        <p className="text-[11px] text-slate-500 mt-1">
                          {flavor.desc}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-14">
            <span className="inline-block text-[11px] font-bold uppercase tracking-widest bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full mb-3">
              4. How The Vote Logistics Work
            </span>
            <h2 className="font-display font-bold text-2xl md:text-3xl text-slate-900 mb-2">
              Store Groupings, Schedule & 2nd Chance
            </h2>
            <p className="text-slate-500 text-sm mb-6 max-w-2xl">
              Over 40 stores are divided alphabetically into 4 groups.
              Categories rotate across every cycle, with 2nd Chance elections in
              May and November.
            </p>

            <div className="bg-white rounded-3xl p-6 shadow-card border border-amber-100 overflow-x-auto mb-6">
              <table className="w-full text-left text-xs sm:text-sm min-w-[560px]">
                <thead>
                  <tr className="text-slate-400 uppercase text-[10px] tracking-wider border-b border-amber-100">
                    <th className="py-3 pr-4 font-bold">Store Group</th>
                    {SCHEDULE_COLUMNS.map((col, index) => (
                      <th
                        key={col}
                        className={
                          index === 2 || index === 5
                            ? "py-3 px-2 text-center font-extrabold text-somi-gold bg-amber-50"
                            : "py-3 px-2 text-center font-bold"
                        }
                      >
                        {col}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-amber-50">
                  {SCHEDULE_ROWS.map((row) => (
                    <tr key={row.group} className="hover:bg-amber-50/50">
                      <td className="py-3 pr-4 font-display font-bold text-slate-900">
                        {row.group}
                      </td>
                      {row.cells.map((cell, index) => (
                        <td key={index} className={scheduleCellClass(cell)}>
                          {FLAVOR_CATEGORIES[cell]?.title || cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="bg-gradient-to-br from-amber-100 to-amber-50 rounded-3xl p-6 md:p-8 shadow-card border border-amber-200/60 flex flex-col md:flex-row items-start md:items-center justify-between gap-5">
              <div className="flex items-start gap-4">
                <span className="text-4xl animate-float">🗳️⚡</span>
                <div>
                  <h3 className="font-display font-bold text-xl text-slate-900">
                    Second Chance Voting (May & November)
                  </h3>
                  <p className="text-sm text-slate-600 mt-1 max-w-xl">
                    Top runner-up flavors from previous voting cycles return for
                    a second chance selection!
                  </p>
                </div>
              </div>
              <button
                onClick={() => selectView("vote")}
                className="bg-somi-waffle hover:bg-somi-gold text-white font-bold px-7 py-3 rounded-full shadow-soft transition-all hover:scale-105 active:scale-95 whitespace-nowrap"
              >
                Go To Voting Booth
              </button>
            </div>
          </section>
        </main>
      )}

      {view === "vote" && (
        <main className="max-w-7xl mx-auto px-4 md:px-8 pb-20">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div>
              <h2 className="font-display font-bold text-2xl md:text-3xl text-slate-900">
                🗳️ Live Store Polls (46 Locations)
              </h2>
              <p className="text-slate-500 text-sm mt-1">
                Stores are split into 4 alphabetical groups, each voting on its
                assigned 3-flavor category. Pick your store to cast your ballot!
              </p>
            </div>
            <div className="flex bg-white border border-amber-200/80 rounded-full p-1 shadow-xs self-start">
              {[
                ["voting", "Voting Open (46 Stores)"],
                ["countdown", "30-Day Countdown"],
              ].map(([mode, label]) => (
                <button
                  key={mode}
                  onClick={() => setCampaignMode(mode)}
                  className={
                    campaignMode === mode
                      ? "px-4 py-1.5 rounded-full text-xs font-semibold transition-all bg-somi-waffle text-white"
                      : "px-4 py-1.5 rounded-full text-xs font-semibold transition-all text-slate-500 hover:text-slate-700"
                  }
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {pollsLoading && (
            <div className="bg-white rounded-3xl p-12 text-center shadow-card border border-amber-100 max-w-xl mx-auto">
              <div className="text-slate-500 text-sm animate-pulse">
                Loading store polls...
              </div>
            </div>
          )}

          {pollsError && (
            <div className="bg-white rounded-3xl p-12 text-center shadow-card border border-amber-100 max-w-xl mx-auto">
              <div className="text-3xl mb-3">⚠️</div>
              <p className="font-bold text-slate-800">
                Could not load store polls
              </p>
              <p className="text-sm text-slate-500 mt-1">{pollsError}</p>
              <button
                onClick={loadPolls}
                className="mt-4 px-6 py-2.5 rounded-full bg-somi-waffle text-white text-xs font-bold hover:bg-somi-gold transition shadow-soft"
              >
                Try Again
              </button>
            </div>
          )}

          {campaignMode === "countdown" ? (
            <div className="bg-white rounded-3xl p-8 md:p-12 shadow-card border border-amber-100 text-center max-w-2xl mx-auto">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto animate-float">
                <Icon name="clock" className="w-8 h-8 text-somi-waffle" />
              </div>
              <span className="inline-block mt-6 text-[11px] font-bold uppercase tracking-widest bg-amber-100 text-somi-gold px-3.5 py-1.5 rounded-full">
                ⏳ Next Cycle Opens Soon
              </span>
              <h3 className="font-display font-bold text-xl md:text-2xl text-slate-900 mt-4">
                Voting Opens In
              </h3>
              <p className="text-slate-500 text-sm max-w-lg mx-auto mt-2">
                The next <strong>Pick Your Somi</strong> voting cycle begins on
                the 1st of the month. Check back when the timer hits zero to
                vote for your store!
              </p>
              <div className="grid grid-cols-4 gap-3 mt-8">
                {[
                  [countdown.days, "Days"],
                  [countdown.hours, "Hours"],
                  [countdown.minutes, "Minutes"],
                  [countdown.seconds, "Seconds"],
                ].map(([value, label]) => (
                  <div key={label} className="bg-amber-50 rounded-2xl py-4">
                    <div className="font-display font-bold text-3xl md:text-4xl text-somi-waffle">
                      {pad(value)}
                    </div>
                    <div className="text-[11px] uppercase tracking-wider text-slate-500 mt-1">
                      {label}
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-8 max-w-md mx-auto flex gap-2">
                <input
                  type="email"
                  value={notifyEmail}
                  onChange={(event) => setNotifyEmail(event.target.value)}
                  placeholder="Enter your email for opening alerts"
                  className="flex-1 px-4 py-3 text-sm rounded-full border border-amber-200/80 bg-white shadow-xs focus:outline-none focus:border-somi-gold focus:ring-2 focus:ring-amber-200 placeholder:text-slate-400"
                />
                <button
                  onClick={submitNotify}
                  className="px-6 py-3 rounded-full bg-somi-waffle text-white text-sm font-bold shadow-soft hover:bg-somi-gold transition-all hover:scale-105 active:scale-95"
                >
                  Notify Me
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="bg-white rounded-2xl p-4 mb-6 shadow-card border border-amber-100 flex items-start gap-3 max-w-3xl">
                <span className="text-lg">ℹ️</span>
                <p className="text-sm text-slate-600">
                  <span className="font-bold text-slate-800">
                    Limit 1 Vote Per Customer:
                  </span>{" "}
                  You may only cast one vote during the voting window. You
                  cannot vote for multiple locations.
                </p>
              </div>

              {userVote && votedFlavor && polls.open.length > 0 && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4 md:p-5 mb-6 flex items-start md:items-center justify-between gap-4 flex-col md:flex-row max-w-3xl">
                  <div className="flex items-start gap-3">
                    <Icon
                      name="check"
                      className="w-6 h-6 text-emerald-500 shrink-0"
                    />
                    <p className="text-sm text-emerald-800">
                      <span className="font-bold">
                        Your ballot is officially locked in!
                      </span>{" "}
                      You voted for {votedFlavor.name} at {userVote.storeName}.
                    </p>
                  </div>
                  <button
                    onClick={() => setResetConfirmOpen(true)}
                    className="flex items-center gap-1.5 text-[11px] font-semibold text-emerald-600 hover:text-emerald-800 underline underline-offset-2 shrink-0"
                  >
                    <Icon name="rotate" className="w-3.5 h-3.5" />
                    Reset Ballot
                  </button>
                </div>
              )}

              <div className="flex flex-col md:flex-row gap-3 mb-6">
                <div className="relative flex-1 max-w-md">
                  <Icon
                    name="search"
                    className="w-4 h-4 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2"
                  />
                  <input
                    value={searchQuery}
                    onChange={(event) => setSearchQuery(event.target.value)}
                    placeholder="Search city or store name..."
                    className="w-full bg-white border border-amber-200/80 rounded-full pl-11 pr-4 py-3 text-sm shadow-xs focus:outline-none focus:border-somi-gold focus:ring-2 focus:ring-amber-200 placeholder:text-slate-400"
                  />
                </div>
                <div className="flex gap-2 overflow-x-auto hide-scrollbar pb-1">
                  {voteStates.map((state) => (
                    <button
                      key={state}
                      onClick={() => setStateFilter(state)}
                      className={
                        stateFilter === state
                          ? "px-3.5 py-1.5 rounded-full text-xs font-bold shrink-0 transition-all bg-somi-waffle text-white shadow-xs"
                          : "px-3.5 py-1.5 rounded-full text-xs font-bold shrink-0 transition-all bg-white text-slate-500 border border-amber-200/80 hover:border-somi-gold"
                      }
                    >
                      {state}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex items-center justify-between mb-4">
                <p className="text-xs text-slate-500 font-medium">
                  {filteredPolls.length} store
                  {filteredPolls.length === 1 ? "" : "s"} found
                </p>
                <div className="flex items-center gap-1.5 text-[11px] text-slate-400">
                  <Icon name="lock" className="w-3.5 h-3.5" />
                  Limit 1 Vote Per Customer
                </div>
              </div>

              {filteredPolls.length === 0 ? (
                <div className="bg-white rounded-3xl p-12 text-center shadow-card border border-amber-100">
                  <div className="text-4xl mb-3">
                    {polls.open.length === 0 ? "🗳️" : "🔍"}
                  </div>
                  <p className="font-bold text-slate-800">
                    {polls.open.length === 0
                      ? "Voting is currently closed"
                      : "No stores found"}
                  </p>
                  <p className="text-sm text-slate-500 mt-1">
                    {polls.open.length === 0
                      ? "Check back when the next cycle opens on the 1st of the month."
                      : "Try a different search or clear the state filter."}
                  </p>
                </div>
              ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filteredPolls.map((poll) => {
                    const isVotedStore = userVote?.storeId === poll.storeId;
                    return (
                      <div
                        key={poll.id}
                        className={
                          isVotedStore
                            ? "bg-white rounded-3xl p-5 shadow-card border border-emerald-300"
                            : "bg-white rounded-3xl p-5 shadow-card border border-amber-100 hover:border-somi-gold transition-all"
                        }
                      >
                        <div className="flex flex-wrap items-center justify-between gap-y-2">
                          <div className="flex flex-wrap items-center gap-2 text-[10px] font-bold uppercase tracking-wider">
                            <span className="bg-amber-100 text-somi-waffle px-2.5 py-1 rounded-full whitespace-nowrap">
                              Group {poll.group}
                            </span>
                            <span className="bg-slate-100 text-slate-500 px-2.5 py-1 rounded-full whitespace-nowrap">
                              {poll.machines}
                            </span>
                            <span className="bg-purple-100 text-purple-700 px-2.5 py-1 rounded-full whitespace-nowrap">
                              {FLAVOR_CATEGORIES[poll.category].title}
                            </span>
                          </div>
                          {isVotedStore && (
                            <span className="text-[10px] font-bold text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full whitespace-nowrap">
                              YOUR VOTE
                            </span>
                          )}
                        </div>
                        <h3 className="font-display font-bold text-lg text-slate-900 mt-3">
                          {poll.storeName}
                        </h3>
                        <p className="text-xs text-slate-500 mt-0.5">
                          {poll.city}, {poll.state}
                        </p>
                        <p className="text-[11px] text-slate-400 mt-2">
                          Votes in:{" "}
                          <span className="font-semibold text-somi-gold">
                            {FLAVOR_CATEGORIES[poll.category].title}
                          </span>
                        </p>
                        <button
                          onClick={() => openVoteModal(poll)}
                          disabled={!!userVote && !isVotedStore}
                          className={
                            isVotedStore
                              ? "w-full mt-4 py-2.5 rounded-full text-sm font-bold bg-emerald-50 text-emerald-600 cursor-default"
                              : userVote
                                ? "w-full mt-4 py-2.5 rounded-full text-sm font-bold bg-slate-100 text-slate-400 cursor-not-allowed"
                                : "w-full mt-4 py-2.5 rounded-full text-sm font-bold bg-somi-waffle text-white hover:bg-somi-gold shadow-soft hover:scale-[1.02] active:scale-95 transition-all"
                          }
                        >
                          {isVotedStore
                            ? "Your Vote Cast"
                            : userVote
                              ? "1 Vote Limit"
                              : "Vote for this Location"}
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </main>
      )}

      {voteModal && (
        <div
          className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm"
          onClick={() => setVoteModal(null)}
        >
          <div
            className="bg-white rounded-3xl max-w-md w-full max-h-[85vh] overflow-y-auto p-6 md:p-8 shadow-soft"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-display font-bold text-xl text-slate-900">
                  Vote for Your Store
                </h3>
                <p className="text-xs text-slate-500 mt-1">
                  SomiSomi - {voteModal.store.name} ({voteModal.store.state}) ·{" "}
                  {voteModal.store.machines} · Group {voteModal.store.group}
                </p>
              </div>
              <button
                onClick={() => setVoteModal(null)}
                className="p-2 rounded-full hover:bg-amber-50 text-slate-400 hover:text-slate-600 transition-colors"
                aria-label="Close"
              >
                <Icon name="x" className="w-5 h-5" />
              </button>
            </div>

            {!voteModal.submitted ? (
              <>
                <div className="mt-5 mb-4 flex items-center justify-between p-2.5 bg-amber-50 rounded-2xl border border-amber-200/80">
                  <span className="text-[11px] font-bold text-somi-gold">
                    {FLAVOR_CATEGORIES[voteModal.store.category].title}
                  </span>
                  <span className="text-[10px] bg-white px-2 py-0.5 rounded font-bold text-slate-600">
                    3 Candidates
                  </span>
                </div>

                <p className="text-[11px] text-slate-400 mb-4 leading-relaxed">
                  Choose 1 of the 3 candidate flavors below. The flavor with the
                  most votes will be loaded into this store's rotational
                  soft-serve slot!
                </p>

                {!user ? (
                  <div className="border border-amber-200/80 bg-amber-50/60 rounded-2xl p-6 text-center">
                    <span className="text-3xl">🔐</span>
                    <p className="font-display font-bold text-slate-800 mt-2">
                      Sign in to cast your vote
                    </p>
                    <p className="text-[11px] text-slate-500 mt-1.5 mb-5 leading-relaxed">
                      Voting is limited to{" "}
                      <span className="font-bold">
                        1 vote per Google account
                      </span>{" "}
                      to keep the election fair. Sign in below to unlock your
                      ballot for {voteModal.store.name}!
                    </p>
                    <GoogleSignInButton
                      onClick={handleSignIn}
                      disabled={signingIn}
                    />
                  </div>
                ) : (
                  <>
                    <div className="flex items-center justify-between gap-2 bg-slate-50 border border-slate-100 rounded-full px-2 py-1 pr-3 mb-4">
                      <div className="flex items-center gap-2 min-w-0">
                        {user.photoURL ? (
                          <img
                            src={user.photoURL}
                            alt=""
                            className="w-6 h-6 rounded-full"
                            referrerPolicy="no-referrer"
                          />
                        ) : (
                          <span className="w-6 h-6 rounded-full bg-amber-100 text-somi-waffle text-[10px] font-bold flex items-center justify-center shrink-0">
                            {(user.displayName || user.email || "?")
                              .charAt(0)
                              .toUpperCase()}
                          </span>
                        )}
                        <span className="text-[11px] font-semibold text-slate-600 truncate">
                          Voting as {user.displayName || user.email}
                        </span>
                      </div>
                      <span className="text-[10px] font-bold text-emerald-600 whitespace-nowrap">
                        ✓ 1 vote per account
                      </span>
                    </div>

                    <div className="space-y-3">
                      {voteModal.store.options.map((flavorId) => {
                        const flavor = FLAVOR_BY_ID[flavorId];
                        return (
                          <button
                            key={flavor.id}
                            onClick={() => castVote(flavor.id)}
                            className="w-full text-left bg-amber-50/70 hover:bg-amber-100 border border-amber-200/80 rounded-2xl p-4 transition-all hover:scale-[1.01] active:scale-[0.99] group"
                          >
                            <div className="flex items-center gap-3">
                              <FlavorDot
                                flavor={flavor}
                                className="w-6 h-6 group-hover:scale-110 transition-transform"
                              />
                              <div className="flex-1">
                                <p className="font-bold text-slate-800 text-sm">
                                  {flavor.name}
                                </p>
                                <p className="text-[11px] text-slate-500">
                                  {flavor.desc}
                                </p>
                              </div>
                              <span className="text-somi-gold opacity-0 group-hover:opacity-100 transition-opacity font-bold text-lg">
                                ✓
                              </span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </>
                )}
              </>
            ) : (
              <div className="text-center py-6">
                <span className="text-4xl">🎉</span>
                <h4 className="font-display font-bold text-slate-900 text-lg mt-2">
                  Thank you for your vote!
                </h4>
                <p className="text-somi-gold font-bold text-xs mt-1">
                  Stay tuned for the results.
                </p>
                <p className="text-slate-500 text-[11px] mt-3 leading-relaxed">
                  Votes are kept confidential during the voting period. Once the
                  poll closes, the winning flavor chosen for your location will
                  be announced and loaded into the machine!
                </p>
                <button
                  onClick={() => {
                    setVoteModal(null);
                    selectView("results");
                  }}
                  className="mt-5 px-6 py-2.5 rounded-full bg-somi-waffle text-white text-xs font-bold hover:bg-somi-gold transition-all shadow-soft"
                >
                  View Winning Flavors
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {resetConfirmOpen && userVote && votedFlavor && (
        <div
          className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm"
          onClick={() => !resettingVote && setResetConfirmOpen(false)}
        >
          <div
            className="bg-white rounded-3xl max-w-sm w-full p-6 md:p-8 shadow-soft text-center"
            onClick={(event) => event.stopPropagation()}
          >
            <span className="text-4xl">🗳️</span>
            <h3 className="font-display font-bold text-xl text-slate-900 mt-3">
              Reset your ballot?
            </h3>
            <p className="text-sm text-slate-500 mt-2 leading-relaxed">
              Your vote for{" "}
              <span className="font-bold text-slate-700">
                {votedFlavor.name}
              </span>{" "}
              at{" "}
              <span className="font-bold text-slate-700">
                {userVote.storeName}
              </span>{" "}
              will be removed. You can vote again while polls are open.
            </p>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setResetConfirmOpen(false)}
                disabled={resettingVote}
                className="flex-1 py-2.5 rounded-full text-sm font-bold bg-slate-100 text-slate-600 hover:bg-slate-200 transition-all disabled:opacity-60"
              >
                Keep My Vote
              </button>
              <button
                onClick={confirmResetVote}
                disabled={resettingVote}
                className="flex-1 py-2.5 rounded-full text-sm font-bold bg-somi-waffle text-white hover:bg-somi-gold transition-all shadow-soft disabled:opacity-60"
              >
                {resettingVote ? "Resetting..." : "Yes, Reset"}
              </button>
            </div>
          </div>
        </div>
      )}

      {view === "results" && (
        <main className="max-w-7xl mx-auto px-4 md:px-8 pb-20">
          <div className="text-center mb-8">
            <span className="inline-block text-[11px] font-bold uppercase tracking-widest bg-amber-100 text-somi-gold px-3.5 py-1.5 rounded-full">
              🍦 Official Selection & Schedule Reveal
            </span>
            <h2 className="font-display font-bold text-2xl md:text-3xl text-slate-900 mt-4">
              Winning Flavors & Swirl Dates by Store
            </h2>
            <p className="text-slate-500 text-sm mt-2 max-w-2xl mx-auto">
              Once voting concludes, each location's chosen flavor and its
              official in-store swirling dates are announced below!
            </p>
          </div>

          <div className="bg-white rounded-2xl p-4 mb-8 shadow-card border border-amber-100 flex items-start gap-3 max-w-3xl mx-auto">
            <span className="text-xl">🔒</span>
            <p className="text-xs text-slate-600 leading-relaxed">
              <span className="font-bold text-slate-800 block">
                Fair Voting & Swirl Schedule Guarantee
              </span>
              Votes are kept strictly confidential with no live counts or
              tallies. Once polls close, this page reveals which flavor won for
              each store and the exact window it will be swirling in the
              dispensers so you can come taste your victory!
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-2 mb-8">
            {resultStates.map((state) => (
              <button
                key={state}
                onClick={() => setResultsState(state)}
                className={
                  resultsState === state
                    ? "px-4 py-1.5 rounded-full text-xs font-bold transition-all bg-somi-waffle text-white shadow-xs"
                    : "px-4 py-1.5 rounded-full text-xs font-bold transition-all bg-white text-slate-500 border border-amber-200/80 hover:border-somi-gold"
                }
              >
                {state === "ALL" ? "All Locations" : state}
              </button>
            ))}
          </div>

          {pollsLoading ? (
            <div className="bg-white rounded-3xl p-12 text-center shadow-card border border-amber-100 max-w-xl mx-auto">
              <div className="text-slate-500 text-sm animate-pulse">
                Loading winning flavors...
              </div>
            </div>
          ) : pollsError ? (
            <div className="bg-white rounded-3xl p-12 text-center shadow-card border border-amber-100 max-w-xl mx-auto">
              <div className="text-3xl mb-3">⚠️</div>
              <p className="font-bold text-slate-800">
                Could not load winning flavors
              </p>
              <p className="text-sm text-slate-500 mt-1">{pollsError}</p>
              <button
                onClick={loadPolls}
                className="mt-4 px-6 py-2.5 rounded-full bg-somi-waffle text-white text-xs font-bold hover:bg-somi-gold transition shadow-soft"
              >
                Try Again
              </button>
            </div>
          ) : filteredResults.length === 0 ? (
            <div className="bg-white rounded-3xl p-12 text-center shadow-card border border-amber-100 max-w-xl mx-auto">
              <div className="text-4xl mb-3">🍦</div>
              <p className="font-bold text-slate-800">
                No winners announced yet
              </p>
              <p className="text-sm text-slate-500 mt-1">
                Winning flavors appear here once the current voting cycle
                closes.
              </p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredResults.map((poll) => {
                const flavor = FLAVOR_BY_ID[poll.winningFlavorId];
                return (
                  <div
                    key={poll.id}
                    className="bg-white rounded-3xl p-5 shadow-card border border-amber-100 hover:border-somi-gold transition-all"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider">
                        <span className="bg-amber-100 text-somi-waffle px-2.5 py-1 rounded-full">
                          Group {poll.group}
                        </span>
                        <span className="bg-emerald-50 text-emerald-600 px-2.5 py-1 rounded-full flex items-center gap-1">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                          Confirmed
                        </span>
                      </div>
                      <FlavorDot flavor={flavor} className="w-6 h-6" />
                    </div>
                    <h3 className="font-display font-bold text-lg text-slate-900 mt-3">
                      {poll.storeName}
                    </h3>
                    <p className="text-xs text-slate-500">
                      {poll.city}, {poll.state} · {poll.machines}
                    </p>
                    <div className="mt-4 pt-4 border-t border-dashed border-amber-200">
                      <p className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">
                        Customer Voted Winner
                      </p>
                      <p className="font-bold text-somi-waffle mt-1">
                        {flavor.name}
                      </p>
                    </div>
                    <div className="flex items-center gap-1.5 mt-3 text-[11px] text-slate-500">
                      <Icon
                        name="calendar"
                        className="w-3.5 h-3.5 text-somi-gold"
                      />
                      Swirling At This Store:{" "}
                      {formatSwirlDate(poll.swirlingStart)} -{" "}
                      {formatSwirlDate(poll.swirlingEnd)}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </main>
      )}

      {deckOpen && (
        <div
          className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm"
          onClick={() => setDeckOpen(false)}
        >
          <div
            className="bg-white rounded-3xl max-w-lg w-full max-h-[85vh] overflow-y-auto p-6 md:p-8 shadow-soft"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-5">
              <div>
                <h3 className="font-display font-bold text-xl text-slate-900">
                  2027 Flavor System Guide
                </h3>
                <p className="text-xs text-slate-500 mt-1">
                  Pick Your Somi Official Deck Notes
                </p>
              </div>
              <button
                onClick={() => setDeckOpen(false)}
                className="p-2 rounded-full hover:bg-amber-50 text-slate-400 hover:text-slate-600 transition-colors"
                aria-label="Close"
              >
                <Icon name="x" className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-3.5 text-xs text-slate-600 leading-relaxed">
              <div className="p-3.5 rounded-2xl bg-amber-50 border border-amber-200/80">
                <h4 className="font-display font-bold text-slate-900 text-sm mb-1">
                  Effective Date
                </h4>
                <p>
                  The new customer-voted rotating flavor program goes live on{" "}
                  <strong>January 1, 2027</strong> across all locations.
                </p>
              </div>
              <div className="p-3.5 rounded-2xl bg-slate-50 border border-slate-200">
                <h4 className="font-display font-bold text-slate-900 text-sm mb-1">
                  Store Baseline Rules
                </h4>
                <p>
                  <strong>3-Machine Stores:</strong> 6 total flavors (4 Core + 2
                  Rotational).
                  <br />
                  <strong>2-Machine Stores:</strong> 4 total flavors (3 Core + 1
                  Rotational).
                </p>
              </div>
              <div className="p-3.5 rounded-2xl bg-slate-50 border border-slate-200">
                <h4 className="font-display font-bold text-slate-900 text-sm mb-1">
                  Rotational Duration
                </h4>
                <p>
                  Customer-voted flavors and seasonal flavors run for 2-month
                  periods. In 3-machine stores, they run at the same time and
                  change monthly.
                </p>
              </div>
            </div>

            <button
              onClick={() => setDeckOpen(false)}
              className="w-full mt-6 py-3 bg-somi-waffle hover:bg-somi-gold text-white font-bold rounded-full transition shadow-soft"
            >
              Close Guide
            </button>
          </div>
        </div>
      )}

      <footer className="bg-white border-t border-amber-100 py-8 text-center text-xs text-slate-500">
        <div className="max-w-7xl mx-auto px-4 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <span className="font-display font-bold text-slate-800">
              SOMISOMI
            </span>
            <span>• 2027 Customer-Voted Flavor Rotation System</span>
          </div>
          <p>
            © 2027 SomiSomi Dessert Inc. Pick Your Somi Official Campaign Hub.
          </p>
        </div>
      </footer>

      <div
        className={
          toast
            ? "fixed bottom-6 right-6 z-[1100] bg-slate-900 text-white text-sm font-medium px-5 py-3.5 rounded-2xl shadow-soft transition-all duration-300 translate-y-0 opacity-100 flex items-center gap-3"
            : "fixed bottom-6 right-6 z-[1100] bg-slate-900 text-white text-sm font-medium px-5 py-3.5 rounded-2xl shadow-soft transition-all duration-300 translate-y-4 opacity-0 pointer-events-none flex items-center gap-3"
        }
      >
        <span className="text-somi-moogi font-display font-bold">
          ✨ Success!
        </span>
        <span>{toast}</span>
      </div>
    </div>
  );
};

export default PickYourSomi;
